// RADIUS Status — the canonical action front-door, POST /v1/api/radius_status/run.
// Every portal-touching RADIUS action goes through {action, args}. Known
// actions today: "client_details", "ledger", "remove_mac_bind",
// "update_client", "list_global_switches" (see internal/radiusbackend/
// scraper/mybilling/mybilling.go). `run` is generic; thin typed wrappers
// per action keep call sites honest.
//
// The page-level endpoints (state / save / test / clear_history) sit
// alongside `run` here too — they're the RADIUS Status & Config page's
// own surface, not generic actions.

import type { ApiClient } from "./client.js";
import type {
  LedgerFull,
  RadiusSaveRequest,
  RadiusSaveResult,
  RadiusStatusState,
  RadiusStatusStateResponse,
  RadiusTestResult,
  RemoveMacBindResult,
  RunResponse,
  UpdateClientResult,
} from "./types.js";

export function makeRadiusStatusApi(client: ApiClient) {
  const run = <T = unknown>(action: string, args?: Record<string, unknown>) =>
    client.post<RunResponse<T>>("/v1/api/radius_status/run", { action, args: args ?? {} });

  return {
    /** Generic escape hatch — prefer the typed wrappers below. */
    run,
    /** The RADIUS Status page view-model: active backend, connection health,
     *  bulk-sync, online-payment kicker, per-action telemetry. Throws if the
     *  `state` envelope is malformed (shouldn't happen — keeps callers honest). */
    async state(): Promise<RadiusStatusState> {
      const r = await client.get<RadiusStatusStateResponse>("/v1/api/radius_status/state");
      if (!r.ok || !r.state) throw new Error(r.error ?? "radius_status state unavailable");
      return r.state;
    },
    /** Persist the backend selection + credentials (admin only). Empty/omitted
     *  `password` keeps the stored ciphertext untouched. */
    save(req: RadiusSaveRequest) {
      const payload: Record<string, unknown> = {
        backend_kind: req.backend_kind,
        base_url: req.base_url,
        username: req.username,
      };
      if (req.password) payload.password = req.password;
      if (req.extra) payload.extra = req.extra;
      return client.post<RadiusSaveResult>("/v1/api/radius_status/save", payload);
    },
    /** Probe connectivity without persisting. Empty `password` falls back to the
     *  stored one, so a URL/username change can be verified in isolation. */
    test(req: RadiusSaveRequest) {
      const payload: Record<string, unknown> = {
        backend_kind: req.backend_kind,
        base_url: req.base_url,
        username: req.username,
      };
      if (req.password) payload.password = req.password;
      if (req.extra) payload.extra = req.extra;
      return client.post<RadiusTestResult>("/v1/api/radius_status/test", payload);
    },
    /** Wipe the rolling telemetry for one (backend, action) pair — admin only.
     *  Pseudo-action keys "login" / "bulk_sync" reset the Connection / Bulk-sync
     *  cards too. */
    clearHistory(action: string) {
      return client.post<RadiusSaveResult>("/v1/api/radius_status/clear_history", { action });
    },
    /** Clear the bound MAC on a subscriber so they can re-auth from a new device. */
    removeMacBind(username: string) {
      return run<RemoveMacBindResult>("remove_mac_bind", { username });
    },
    /** Update a subscriber's mobile and/or switch on MyBilling. Only pass the
     *  fields you want changed — `undefined` ones are omitted from the JSON, so
     *  the backend leaves those alone (partial-update-safe). */
    updateClient(args: { username: string; mobile1?: string; switch_name?: string }) {
      const payload: Record<string, unknown> = { username: args.username };
      if (args.mobile1 !== undefined) payload.mobile1 = args.mobile1;
      if (args.switch_name !== undefined) payload.switch_name = args.switch_name;
      return run<UpdateClientResult>("update_client", payload);
    },
    /** Pull a subscriber's billing ledger from MyBilling — the totals
     *  summary + the (sanitised) HTML detail table. Slow (~17s portal
     *  scrape); call on demand, not on render. */
    ledger(username: string) {
      return run<LedgerFull>("ledger", { username });
    },
  };
}

export type RadiusStatusApi = ReturnType<typeof makeRadiusStatusApi>;
